import { CreateSearchAlert } from "@/interfaces/search-alert.interface";
import {
  createSearchAlert,
  deleteSearchAlert,
  fetchAllSearchAlerts,
  unSubscribeAllSearchAlerts,
  unSubscribeSearchAlert,
  updateSearchAlert,
} from "@/lib/api/search-alert";
import { useAuth } from "@clerk/nextjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const useCreateSearchAlert = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async (variables: CreateSearchAlert) => {
      const token = await getToken();
      return createSearchAlert(token!, variables);
    },
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ["SEARCH_ALERTS"],
      });
    },
  });
};

export const useEditSearchAlert = (id: string) => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async (variables: Omit<CreateSearchAlert, "filters">) => {
      const token = await getToken();
      return updateSearchAlert(token!, id, variables);
    },
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ["SEARCH_ALERTS"],
      });
    },
  });
};

export const useDeleteSearchAlert = (id: string) => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return deleteSearchAlert(token!, id);
    },
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ["SEARCH_ALERTS"],
      });
    },
  });
};

export const useUnsubscribeSearchAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: { id: string; userId: string }) => {
      return unSubscribeSearchAlert(variables.id, variables.userId);
    },
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ["SEARCH_ALERTS"],
      });
    },
  });
};

export const useUnsubscribeAllSearchAlerts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: { userId: string }) => {
      return unSubscribeAllSearchAlerts(variables.userId);
    },
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ["SEARCH_ALERTS"],
      });
    },
  });
};

export const useSearchAlerts = () => {
  const { getToken, isSignedIn, isLoaded } = useAuth();

  return useQuery({
    queryKey: ["SEARCH_ALERTS"],
    queryFn: async () => {
      const token = await getToken();
      return await fetchAllSearchAlerts(token!);
    },
    enabled: isLoaded && isSignedIn,
  });
};
